const _excluded = ["showAccountsInHeader", "onLogout", "helpLinks", "isProductRequired", "isAccountView", "onProductClick", "onProjectClick", "onAccountClick"];
function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
function _objectWithoutProperties(source, excluded) { if (source == null) return {}; var target = _objectWithoutPropertiesLoose(source, excluded); var key, i; if (Object.getOwnPropertySymbols) { var sourceSymbolKeys = Object.getOwnPropertySymbols(source); for (i = 0; i < sourceSymbolKeys.length; i++) { key = sourceSymbolKeys[i]; if (excluded.indexOf(key) >= 0) continue; if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue; target[key] = source[key]; } } return target; }
function _objectWithoutPropertiesLoose(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }
import React, { useEffect, useRef, useMemo } from 'react';
import PropTypes from 'prop-types';
import { PRODUCT_IDS, useAccountEntitlements, useAlloyFeatureFlag, useCurrentAccountId, useCurrentProject, useCurrentProjectId, useCurrentProjectIsTemplate, useProjectEntitlements, useUser, useCurrentProductId } from '@adsk/alloy-provider';
import TopNavigation from '@adsk/alloy-top-navigation';
import getProductPickerDividers from './helpers/getProductPickerDividers';
import { initialize as initializePendo } from './pendo';
import ProductPickerHeader from './ProductPickerHeader';
import { useProducts } from './useProducts';
import { useProductsAccessTypes } from './useProductsAccessTypes';
const ConnectedTopNavigation = _ref => {
  let {
      showAccountsInHeader,
      onLogout,
      helpLinks,
      isProductRequired,
      isAccountView,
      onProductClick,
      onProjectClick,
      onAccountClick
    } = _ref,
    otherProps = _objectWithoutProperties(_ref, _excluded);
  const currentProject = useCurrentProject();
  const {
    UNSAFE_projects: projectsAndTemplates,
    loading: projectsLoading,
    error: projectsError,
    retry: projectsRetry
  } = useProjectEntitlements();
  const {
    projects,
    templates
  } = useMemo(() => {
    const validProjects = projectsAndTemplates === null || projectsAndTemplates === void 0 ? void 0 : projectsAndTemplates.filter(p => !!p.defaultUrl);
    return {
      projects: validProjects === null || validProjects === void 0 ? void 0 : validProjects.filter(p => p.classification !== 'template'),
      templates: validProjects === null || validProjects === void 0 ? void 0 : validProjects.filter(p => p.classification === 'template')
    };
  }, [projectsAndTemplates]);
  const {
    data: accountsData
  } = useAccountEntitlements();
  const currentProductId = useCurrentProductId();
  const {
    user
  } = useUser();
  const currentAccountId = useCurrentAccountId();
  const currentProjectId = useCurrentProjectId();
  const currentProjectIsTemplate = useCurrentProjectIsTemplate();
  const accounts = accountsData === null || accountsData === void 0 ? void 0 : accountsData.accounts;
  const products = useProducts();
  const {
    value: showProductPickerWithHeader
  } = useAlloyFeatureFlag('show-product-picker-with-header');
  const isPendoInitialized = useRef(false);
  const {
    data: productsAccessTypesData,
    loading: productsAccessTypesLoading
  } = useProductsAccessTypes();
  useEffect(() => {
    if (!productsAccessTypesLoading && user && (currentProject || isAccountView) && !isPendoInitialized.current) {
      var _currentProject$acces, _currentProject$acces2;
      const licenses = productsAccessTypesData;
      const currentProductLicense = licenses === null || licenses === void 0 ? void 0 : licenses.find(_ref2 => {
        let {
          productType
        } = _ref2;
        return productType === currentProductId;
      });
      initializePendo({
        user,
        context: {
          is_project_template: currentProjectIsTemplate,
          is_admin: currentProject === null || currentProject === void 0 ? void 0 : (_currentProject$acces = currentProject.accessLevels) === null || _currentProject$acces === void 0 ? void 0 : _currentProject$acces.projectAdministrator,
          is_on_trial: (currentProductLicense === null || currentProductLicense === void 0 ? void 0 : currentProductLicense.type) === 'trial',
          account_id: currentAccountId,
          project_id: currentProjectId,
          project_classification: currentProject === null || currentProject === void 0 ? void 0 : currentProject.classification,
          account_name: currentProject === null || currentProject === void 0 ? void 0 : currentProject.accountName,
          is_acct_admin: currentProject === null || currentProject === void 0 ? void 0 : (_currentProject$acces2 = currentProject.accessLevels) === null || _currentProject$acces2 === void 0 ? void 0 : _currentProject$acces2.accountAdministrator
        }
      });
      isPendoInitialized.current = true;
    }
  }, [currentAccountId, currentProject, currentProjectId, currentProjectIsTemplate, currentProductId, isAccountView, productsAccessTypesData, productsAccessTypesLoading, user]);
  return /*#__PURE__*/React.createElement(TopNavigation, _extends({
    accountId: currentAccountId,
    accounts: showAccountsInHeader ? accounts : undefined,
    projectId: currentProjectId,
    UNSAFE_templates: templates,
    UNSAFE_projects: projects,
    UNSAFE_projectsLoading: projectsLoading,
    UNSAFE_projectsError: !!projectsError,
    UNSAFE_projectsRetry: projectsRetry,
    currentProject: currentProject,
    currentProductId: currentProductId,
    templateId: currentProjectId,
    products: getProductPickerDividers(products, showProductPickerWithHeader),
    user: user,
    onLogout: onLogout,
    showHelpMenu: true,
    admin: [PRODUCT_IDS.PROJECT_ADMINISTRATION, PRODUCT_IDS.ACCOUNT_ADMINISTRATION].includes(currentProductId),
    helpLinks: helpLinks,
    showTrialBanner: true,
    isProductRequired: isProductRequired,
    renderCustomProductHeader: showProductPickerWithHeader && !isAccountView ? () => /*#__PURE__*/React.createElement(ProductPickerHeader, null) : undefined,
    onProductClick: onProductClick,
    onProjectClick: onProjectClick,
    onAccountClick: onAccountClick
  }, otherProps));
};
ConnectedTopNavigation.propTypes = {
  /** choose between Project Picker Account view or Project View */
  showAccountsInHeader: PropTypes.bool,
  /** callback called on logout */
  onLogout: PropTypes.func,
  /** Override default help links */
  helpLinks: TopNavigation.propTypes.helpLinks,
  /** is product required */
  isProductRequired: PropTypes.bool,
  /** is account view */
  isAccountView: PropTypes.bool,
  /** Custom product click handler */
  onProductClick: PropTypes.func,
  /** Custom project click handler */
  onProjectClick: PropTypes.func,
  /** Custom account click handler */
  onAccountClick: PropTypes.func
};
export { ConnectedTopNavigation };
//# sourceMappingURL=ConnectedTopNavigation.js.map